import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
  RefreshControl,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { useThemeColors } from "@/providers/ThemeProvider";
import { FamilyWeekend } from "@/types";

/**
 * Weekend management item component
 */
interface WeekendItemProps {
  weekend: FamilyWeekend;
  onPress: () => void;
  onActivate: () => void;
}

const WeekendItem = ({ weekend, onPress, onActivate }: WeekendItemProps) => {
  const colors = useThemeColors();

  return (
    <View
      className="mb-3 rounded-xl overflow-hidden"
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
      }}
    >
      <Pressable
        onPress={onPress}
        className="px-4 py-4 flex-row items-center active:opacity-70"
        accessibilityRole="button"
        accessibilityLabel={`Bewerk ${weekend.name}`}
      >
        <View className="w-12 h-12 rounded-full bg-red-100 items-center justify-center mr-3">
          <Text className="text-xl">🎉</Text>
        </View>
        <View className="flex-1">
          <View className="flex-row items-center flex-wrap">
            <Text className="text-base font-semibold" style={{ color: colors.text }}>
              {weekend.name}
            </Text>
            {weekend.is_active && (
              <View className="ml-2 bg-green-100 px-2 py-0.5 rounded-full">
                <Text className="text-xs text-green-700 font-medium">Actief</Text>
              </View>
            )}
            {weekend.is_finalized && (
              <View className="ml-2 bg-purple-100 px-2 py-0.5 rounded-full">
                <Text className="text-xs text-purple-700 font-medium">Afgerond</Text>
              </View>
            )}
          </View>
          <Text className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>
            {weekend.year}
          </Text>
          {weekend.theme && (
            <Text className="text-xs mt-0.5" style={{ color: colors.primary }}>
              {weekend.theme}
            </Text>
          )}
        </View>
        <Ionicons name="pencil-outline" size={20} color={colors.textTertiary} />
      </Pressable>

      {/* Action buttons */}
      {!weekend.is_active && (
        <View
          className="px-4 py-2"
          style={{ borderTopWidth: 1, borderTopColor: colors.border }}
        >
          <Pressable
            onPress={onActivate}
            className="flex-row items-center justify-center py-2 rounded-lg active:opacity-80"
            style={{ backgroundColor: "#D1FAE5" }}
            accessibilityRole="button"
            accessibilityLabel={`Activeer ${weekend.name}`}
          >
            <Ionicons name="checkmark-circle-outline" size={18} color="#16A34A" />
            <Text className="font-medium ml-2" style={{ color: "#15803D" }}>
              Activeer dit weekend
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  );
};

/**
 * Weekends management screen
 */
export default function WeekendsManagementScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const [weekends, setWeekends] = useState<FamilyWeekend[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  /**
   * Fetch all weekends
   */
  const fetchWeekends = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("family_weekends")
        .select("*")
        .order("year", { ascending: false });

      if (!error && data) {
        setWeekends(data);
      }
    } catch (error) {
      console.error("Error fetching weekends:", error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchWeekends();
  }, [fetchWeekends]);

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchWeekends();
  };

  /**
   * Handle create new weekend
   */
  const handleCreateWeekend = () => {
    router.push("/(super-admin)/weekends/create");
  };

  /**
   * Handle edit weekend
   */
  const handleEditWeekend = (weekend: FamilyWeekend) => {
    router.push(`/(super-admin)/weekends/${weekend.id}`);
  };

  /**
   * Handle activate weekend
   */
  const handleActivateWeekend = (weekend: FamilyWeekend) => {
    const activeWeekend = weekends.find((w) => w.is_active);

    Alert.alert(
      "Weekend Activeren",
      `Weet je zeker dat je "${weekend.name}" wilt activeren?\n\n` +
        (activeWeekend
          ? `Dit zal "${activeWeekend.name}" deactiveren en alle Familie Admin rechten van dat weekend intrekken.`
          : "Dit weekend wordt het actieve weekend."),
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Activeren",
          onPress: () => executeActivateWeekend(weekend, activeWeekend),
        },
      ]
    );
  };

  /**
   * Execute the activation
   */
  const executeActivateWeekend = async (
    weekend: FamilyWeekend,
    previousActiveWeekend: FamilyWeekend | undefined
  ) => {
    setIsActivating(true);

    try {
      // Step 1: Deactivate all weekends
      const { error: deactivateError } = await supabase
        .from("family_weekends")
        .update({ is_active: false })
        .eq("is_active", true);

      if (deactivateError) throw deactivateError;

      // Step 2: Activate the new weekend
      const { error: activateError } = await supabase
        .from("family_weekends")
        .update({ is_active: true })
        .eq("id", weekend.id);

      if (activateError) throw activateError;

      // Step 3: Revoke familie_admin roles from previous weekend
      if (previousActiveWeekend) {
        const { error: revokeError } = await supabase
          .from("user_roles")
          .update({
            revoked_at: new Date().toISOString(),
          })
          .eq("weekend_id", previousActiveWeekend.id)
          .eq("role", "familie_admin")
          .is("revoked_at", null);

        if (revokeError) {
          console.error("Error revoking roles:", revokeError);
        }
      }

      Alert.alert("Succes", `"${weekend.name}" is nu het actieve weekend.`);
      fetchWeekends();
    } catch (error) {
      console.error("Error activating weekend:", error);
      Alert.alert("Fout", "Kon weekend niet activeren");
    } finally {
      setIsActivating(false);
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ActivityIndicator size="large" color="#DC2626" />
        <Text className="mt-2" style={{ color: colors.textSecondary }}>
          Weekenden laden...
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      {/* Create Button */}
      <View
        className="px-4 py-3"
        style={{
          backgroundColor: colors.surface,
          borderBottomWidth: 1,
          borderBottomColor: colors.border,
        }}
      >
        <Pressable
          onPress={handleCreateWeekend}
          className="rounded-xl py-3 flex-row items-center justify-center active:opacity-80"
          style={{ backgroundColor: "#DC2626" }}
          accessibilityRole="button"
          accessibilityLabel="Nieuw weekend aanmaken"
        >
          <Ionicons name="add-circle-outline" size={20} color="#FFFFFF" />
          <Text className="text-white font-semibold ml-2">Nieuw Weekend Aanmaken</Text>
        </Pressable>
      </View>

      {/* Weekends Count */}
      <View className="px-4 py-2" style={{ backgroundColor: colors.background }}>
        <Text className="text-sm" style={{ color: colors.textSecondary }}>
          {weekends.length} weekend{weekends.length !== 1 ? "s" : ""}
        </Text>
      </View>

      {/* Weekends List */}
      <ScrollView
        className="flex-1 px-4"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor="#DC2626"
          />
        }
      >
        {weekends.length > 0 ? (
          weekends.map((weekend) => (
            <WeekendItem
              key={weekend.id}
              weekend={weekend}
              onPress={() => handleEditWeekend(weekend)}
              onActivate={() => handleActivateWeekend(weekend)}
            />
          ))
        ) : (
          <View className="flex-1 items-center justify-center py-12">
            <Ionicons name="calendar-outline" size={48} color={colors.border} />
            <Text className="mt-2" style={{ color: colors.textTertiary }}>
              Nog geen weekenden
            </Text>
            <Text className="text-sm" style={{ color: colors.textTertiary }}>
              Maak een nieuw weekend aan
            </Text>
          </View>
        )}

        <View className="h-4" />
      </ScrollView>

      {/* Loading overlay */}
      {isActivating && (
        <View className="absolute inset-0 bg-black/20 items-center justify-center">
          <View
            className="rounded-xl px-6 py-4 flex-row items-center"
            style={{ backgroundColor: colors.surface }}
          >
            <ActivityIndicator size="small" color="#DC2626" />
            <Text className="ml-3" style={{ color: colors.text }}>
              Weekend activeren...
            </Text>
          </View>
        </View>
      )}
    </SafeAreaView>
  );
}
